import React, { useMemo, ReactNode } from "react"
import { OutlinedButton, ProgressList } from "dash-styled"
import { useDispatch } from "react-redux"
import {
    onboardingBackToChooseOrganization,
    onboardingBackToInput,
    onboardingBackToLabelerSelect,
    onboardingBackToParameters
} from "../../../../../Actions/API/AppTemplate/Interface/TemplateInstantiateActions"
import { TemplateInstantiateStage } from "../../../../../Models/Client/TemplateInstantiation"
import { useUserPlan } from "../../../../../Hooks/useUserPlan"
import { UserPlan } from "../../../../../Models/API/UserModel"
import { useAppState } from "../../../../../Hooks/useReduxState"
import { CompanyLogo } from "../../../../Common/CompanyLogo/CompanyLogo"
import { ReactComponent as BackArrow } from "icons/lib/back-arrow.svg"
import { useOrgs } from "../../../../../Hooks/useOrgs"
import { hasValue } from "../../../../../Models/Client/FetchState"

interface OnboardingProgressProps {
    currentStage: TemplateInstantiateStage
    back?: boolean
    onBack?: () => void
    noMargin?: boolean
}

const BackArrowIcon = () => <BackArrow className="w-3 h-3 mr-2" />

export function OnboardingProgress({
    currentStage,
    back,
    onBack,
    noMargin = false
}: OnboardingProgressProps) {
    const dispatch = useDispatch()
    const userPlan = useUserPlan()
    const orgsFetch = useOrgs()
    const onboarding = useAppState(
        state => state.api.interface.templateInstantiate
    )

    const isFullUser =
        userPlan === UserPlan.FULL && onboarding?.aiWorkflow !== true
    const showOrgChooser = hasValue(orgsFetch) && orgsFetch.value.length > 0

    const getProgressIndex = (currentStage: TemplateInstantiateStage) => {
        switch (currentStage) {
            case TemplateInstantiateStage.setUser:
                return 0
            case TemplateInstantiateStage.orgChoose:
                return isFullUser && showOrgChooser ? 1 : 0
            case TemplateInstantiateStage.selectLabeler:
                return isFullUser ? (showOrgChooser ? 2 : 1) : 0
            case TemplateInstantiateStage.createJob:
            case TemplateInstantiateStage.selectProject:
                return isFullUser ? (showOrgChooser ? 3 : 2) : 1
            case TemplateInstantiateStage.setParameters:
                return isFullUser ? (showOrgChooser ? 4 : 3) : 2
            default:
                return isFullUser ? (showOrgChooser ? 5 : 4) : 3
        }
    }
    const progress = getProgressIndex(currentStage)

    const progressList = useMemo(() => {
        const components: { component: ReactNode; onBackTo?: () => void }[] = []
        if (isFullUser) {
            if (showOrgChooser) {
                components.push({
                    component: <>Choose organization</>,
                    onBackTo: () =>
                        dispatch(onboardingBackToChooseOrganization())
                })
            }
            components.push({
                component: <>Who will process</>,
                onBackTo: () => dispatch(onboardingBackToLabelerSelect())
            })
        }
        return components.concat([
            {
                component: <>Upload data</>,
                onBackTo: () => dispatch(onboardingBackToInput())
            },
            {
                component: <>Configure design</>,
                onBackTo: () => dispatch(onboardingBackToParameters())
            },
            {
                component: (
                    <>
                        {onboarding?.canoticCrowd || onboarding?.aiWorkflow
                            ? "Check quality and refine"
                            : "Start processing"}
                    </>
                )
            }
        ])
    }, [dispatch, onboarding, showOrgChooser, isFullUser])

    return (
        <div
            className={`flex items-center w-full px-5 bg-grape-900 border-b border-smoke-600 ${
                noMargin ? "" : "mb-7"
            }`}
        >
            <div className="w-1/4">
                {back && (
                    <OutlinedButton
                        label="Go back"
                        testId="onboarding-back-button"
                        className="bg-grape-900 text-gray-200 py-3"
                        onClick={onBack}
                        disabled={onBack === undefined}
                        Icon={BackArrowIcon}
                    >
                        Go back
                    </OutlinedButton>
                )}
            </div>
            <ProgressList
                variant="horizontal"
                progress={progress}
                className="w-1/2 pt-4 justify-center"
                occupyAvailableSpace={false}
            >
                {progressList.map(({ component, onBackTo }, index) => {
                    const handleClick =
                        onBackTo === undefined || index >= progress - 1
                            ? undefined
                            : onBackTo
                    const disabled = handleClick === undefined

                    return (
                        <button
                            key={index}
                            className={[
                                "text-grape-50 block w-38 px-1 text-xs text-center pt-12 -mt-13 pb-3",
                                disabled ? "cursor-default" : ""
                            ].join(" ")}
                            disabled={disabled}
                            onClick={handleClick}
                        >
                            {component}
                        </button>
                    )
                })}
            </ProgressList>
            <div className="w-1/4 flex justify-end items-center">
                <div className="w-6">
                    <CompanyLogo variant="symbol" size="sm" />
                </div>
            </div>
        </div>
    )
}
